// Owner: shell-nav. Pinned items of the rail: pin the open page, unpin, reorder (optimistic, with the
// server's answer as the truth).
import { useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { pinItemContract, reorderPinsContract, unpinItemContract } from '@dataroom/contracts/nav';
import type { NavDTO, NavItem, NavItemRef } from '@dataroom/core';
import { useToast } from '@dataroom/ui';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { qk, useNav } from '../lib/queries';
import { recentRefOf } from './recent';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether a pinned item is the one a reference points to (views by id or key). */
export function pinMatches(item: NavItem, ref: NavItemRef): boolean {
  if (item.kind === 'view' && ref.kind === 'view')
    return (
      item.objectKey === ref.objectKey &&
      (item.id === ref.id || item.href.endsWith(`view=${encodeURIComponent(ref.id)}`))
    );
  const kinds = ['page', 'deck', 'document'];
  const sameKind = item.kind === ref.kind || (kinds.includes(item.kind) && kinds.includes(ref.kind));
  return sameKind && item.id === ref.id;
}

export function usePins() {
  const qc = useQueryClient();
  const toast = useToast();
  const nav = useNav().data;
  const pinned = nav?.pinned ?? [];
  const setPinned = (next: NavItem[]) =>
    qc.setQueryData<NavDTO>(qk.nav, (old) => (old ? { ...old, pinned: next } : old));
  const failed = () => {
    toast({ title: t('shell.pin.failed'), tone: 'err' });
    void qc.invalidateQueries({ queryKey: qk.nav });
  };

  const pin = async (ref: NavItemRef) => {
    try {
      const res = await api.call(pinItemContract, { body: ref });
      setPinned(res.pinned);
      toast({ title: t('shell.pin.done'), tone: 'ok' });
      // A pinned view is a favourite of its list too.
      if (ref.kind === 'view') void qc.invalidateQueries({ queryKey: ['views'] });
    } catch {
      failed();
    }
  };
  const unpin = async (item: NavItem) => {
    const before = pinned;
    setPinned(pinned.filter((p) => p.key !== item.key));
    try {
      if (!UUID.test(item.id)) throw new Error('not an id');
      await api.call(unpinItemContract, { params: { kind: item.kind, id: item.id } });
      toast({
        title: t('shell.pin.removed', { name: item.label }),
        tone: 'info',
        undo: async () => {
          const ref: NavItemRef =
            item.objectKey && (item.kind === 'record' || item.kind === 'view')
              ? { kind: item.kind, id: item.id, objectKey: item.objectKey }
              : { kind: item.kind, id: item.id };
          const res = await api.call(pinItemContract, { body: ref });
          // Back in its place.
          const keys = before.map((p) => p.key);
          const restored = await api.call(reorderPinsContract, { body: { keys } }).catch(() => res);
          setPinned(restored.pinned);
        },
      });
      void qc.invalidateQueries({ queryKey: ['views'] });
    } catch {
      setPinned(before);
      failed();
    }
  };
  const reorder = async (next: NavItem[]) => {
    setPinned(next);
    try {
      const res = await api.call(reorderPinsContract, { body: { keys: next.map((p) => p.key) } });
      setPinned(res.pinned);
    } catch {
      failed();
    }
  };
  return { pinned, pin, unpin, reorder };
}

/** The pin state of the open route. */
export function useCurrentPin() {
  const location = useRouterState({ select: (s) => s.location });
  const pins = usePins();
  const ref = recentRefOf(location.pathname, location.search);
  const item = ref ? pins.pinned.find((p) => pinMatches(p, ref)) : undefined;
  return {
    ref,
    pinned: Boolean(item),
    toggle: () => {
      if (!ref) return;
      if (item) void pins.unpin(item);
      else void pins.pin(ref);
    },
  };
}
